import Konva from 'konva';
import HaspObject from './HaspObject';
import { range } from '../utils/utils';
import { interpolateColor } from '../utils/color';
import { processIconText } from '../utils/iconFonts';
import { FontBinding } from '../services/FontBinding.js';
import { useFontStore } from '../stores/fontStore.js';
import { contentBox, onPaddingStyle } from './properties/v2/padding.js';
import { createContentClipGroup } from './contentClip.js';

// min	        int16	0	minimum value of the indicator
// max	        int16	100	maximum value of the indicator
// val	        int16	0	current value of the indicator
// angle	    0-360	240	angle between start and end of the scale
// line_count	uint16	31	tick count of the scale
// rotation	    0-360	0	offset for the scale angles to rotate it
// type	        0-1	    0	0 = indicator lines are activated clock-wise
//                          1 = indicator lines are activated counter-clock-wise

// main's line_width is the width of the indicator lines

export default class HaspLineMeter extends HaspObject {
    rot_offset = -90;

    hasp_type_options = [
        { value: 0, description: "clock-wise" },
        { value: 1, description: "counter-clock-wise" }
    ]

    constructor(config) {
        super(config);

        this._valueFontBinding = new FontBinding((family) => {
            this.valueText.fontFamily(family);
            this.valueText.getLayer()?.batchDraw();
        });

        this.on('transform', function () {
            this.drawLines();
            this.textPosition();
        });

        this.on('transformend', function () {
            this.drawLines();
            this.size(this.rect.size());
            this.textPosition();
        });

        this.onStyle('main', 'line_color', () => {
            this.drawLines();
        })

        this.onStyle('main', 'line_width', () => {
            this.drawLines();
        })

        // Shorthand + all four per-side overrides, one subscription helper.
        onPaddingStyle(this, 'main', () => {
            this.drawLines();
        })

        this.onStyle('main', 'scale_grad_color', () => {
            this.drawLines();
        })

        this.on('value_color', (e) => {
            this.valueText.fill(this.value_color);
        })

        this.on('value_str', (e) => {
            this.valueText.text(processIconText(this.value_str));
        });

        this.on('value_font', (e) => {
            this.valueText.fontSize(this.value_font);
        });

        this.on('value_font_resource', () => {
            this._applyValueFont();
        });

        this.onStyle('main', 'keep_circle', (e) => {
            // if (this.keepCircle) this.radius = 999;
            // else this.radius = this.height() / 2;
            this.rect.cornerRadius(this.style('main').radius);
        });

        this.on('destroy', () => {
            this._valueFontBinding.dispose();
        });

        this.draggable(true);

        // LVGL passes the linemeter widget rectangle as clip_area to both its
        // background and scale drawing. Internal Designer content follows the
        // same rectangular mask while selection remains on the owner/layer.
        this.contentGroup = createContentClipGroup(this);

        this.drawLines();
        this.createText();
        this._applyValueFont();
    }

    /**
     * The uploaded Font Resource `this.value_font_resource` resolves to for
     * canvas preview — see HaspGauge.js's identically-named helper for the
     * full semantics.
     */
    _valueFontResource() {
        return this.value_font_resource ? useFontStore().fonts.find(f => f.file === this.value_font_resource) : null;
    }

    /**
     * Resolve which font family the value text (this.valueText) should
     * render with. Falls back to 'Material Icons' (the pre-existing
     * hardcoded default) when no Font Resource is selected.
     */
    _applyValueFont() {
        this._valueFontBinding.set(this._valueFontResource(), 'Material Icons');
    }

    setNewSize(size) {
        super.setNewSize(size);
        this.drawLines();
        this.textPosition();
    }

    drawLines() {
        // console.log(this)
        if (this.lineGroup !== undefined)
            this.lineGroup.destroy()
        // this.lineGroup = new Konva.Group({
        //     x: 0,
        //     y: 0,
        //     width: this.rect.width(),
        //     height: this.rect.height(),
        // })

        const main = this.style('main');
        // Effective content box (per-side padding, see v2/padding.js).
        const box = contentBox(main, this.width(), this.height());
        const halfWidth = box.width / 2;

        this.lineGroup = new Konva.Group({
            x: box.x,
            y: box.y,
            width: this.rect.width(),
            height: this.rect.height(),
            listening: false,
        })

        // const halfWidth = this.rect.width() / 2;
        const outerRadius = halfWidth;
        const smallInnerRadius = outerRadius - 15;
        const tickValues = range(this.min, this.max, this.line_count)
        var angle = 0;


        const correctionAngle = this.rot_offset - (-this.hasp_rotation) - (this.hasp_angle / 2);
        tickValues.forEach(i => {
            angle = (((i - this.min) / (this.max - this.min)) * this.hasp_angle) + correctionAngle;
            var linecolor = this.theme?.tick_color ?? '#888888';
            var strokeWidth = 4;
            if (i < this.val) {
                const ratio = (i - this.min) / (this.max - this.min);
                linecolor = interpolateColor(main.line_color, main.scale_grad_color, ratio);
                strokeWidth = main.line_width;
            }
            this.drawTick(angle, halfWidth, halfWidth, smallInnerRadius, outerRadius, strokeWidth, linecolor);
        });

        this.contentGroup.add(this.lineGroup);
    }

    drawTick(angle, x, y, innerRadius, outerRadius, strokeWidth, linecolor) {
        let x1 = (Math.cos(angle * (Math.PI / 180)) * innerRadius);
        let y1 = (Math.sin(angle * (Math.PI / 180)) * innerRadius);
        let x2 = (Math.cos(angle * (Math.PI / 180)) * outerRadius);
        let y2 = (Math.sin(angle * (Math.PI / 180)) * outerRadius);

        this.lineGroup.add(new Konva.Line({
            x: x,
            y: y,
            points: [x1, y1, x2, y2],
            stroke: linecolor,
            obj: 'linemetergroup',
            strokeWidth: strokeWidth,
            tension: 0,
            listening: false,
        }));
    }

    createText() {
        this.labelPos = this.width() / 2;
        const halfHeight = this.height() / 2;
        this.valueText = new Konva.Text({
            x: 0,
            // width: halfWidth,
            // height: halfHeight,
            verticalAlign: 'middle',
            align: 'center',
            text: processIconText(this.value_str),
            fontSize: this.value_font,
            fontFamily: 'Material Icons',
            fill: this.value_color,
            listening: true,
            draggable: true,
            strokeWidth: 1,
            obj: 'value_text',
        });

        this.contentGroup.add(this.valueText);
        this.valueText.x((this.width() / 2) - (this.valueText.width() / 2) + (this.value_ofs_x || 0));
        this.valueText.y((this.height() / 2) - (this.valueText.height() / 2) + (this.value_ofs_y || 0));

        this.valueText.on('dragstart', (e) => {
            // console.log('drag start')
            this.textBounds();
        });
        this.valueText.on('dragmove', (e) => {
            // console.log('drag move')
            this.textBounds();
        });
        this.valueText.on('dragend', (e) => {
            // console.log('drag end')
            this.textBounds();
        });
    }

    textBounds() {
        if (this.valueText !== undefined) {
            if (this.valueText.x() < 0) this.valueText.x(0);
            if (this.valueText.x() > this.width() - this.valueText.width()) this.valueText.x(this.width() - this.valueText.width());
            if (this.valueText.y() < 0) this.valueText.y(0);
            if (this.valueText.y() > this.height() - this.valueText.height()) this.valueText.y(this.height() - this.valueText.height());
        }
    }

    textPosition() {
        if (this.valueText !== undefined) {
            var scaledWidth = this.valueText.x() * this.scaledX;
            this.valueText.x(scaledWidth);
            const scaledHeight = this.valueText.y() * this.scaledY;
            this.valueText.y(scaledHeight);
            this.textBounds();
        }
    }

    adjust(event) {
        this.val = Number(this.val);
        this.min = Number(this.min);
        this.max = Number(this.max);
        if (this.val > this.max)
            this.val = this.max;
        if (this.val < this.min)
            this.val = this.min
        this.drawLines();
        // this.createText();
        super.adjust(event);
    }

    // objectExport(page, objectData) {
    //     // objectData.min = this.min;
    //     // objectData.max = this.max;
    //     // if (this.val > 0) objectData.val = this.val;
    //     if (this.line_count > 0) objectData.line_count = this.line_count;
    //     // if (this.text !== '') objectData.value_str = this.text;
    //     // if (this.type !== 0) objectData.type = this.type;
    //     // if (this.angle !== 240) objectData.angle = this.angle;
    //     // if (this.hasp_rotation > 0) objectData.rotation = this.hasp_rotation;
    //     // if (this.value_str !== '') {
    //     // objectData.value_str = this.value_str;
    //     // }
    // }

    objectExport(page, objectData, hassConfig) {
        super.objectExport(page, objectData, hassConfig);
        if (this.value_str !== '') {
            objectData.value_ofs_x = this.valueText.x() + (this.valueText.width() / 2) - (this.width() / 2);
            objectData.value_ofs_y = this.valueText.y() + (this.valueText.height() / 2) - (this.height() / 2);
        }
        if (this.line_count > 0) objectData.line_count = this.line_count;
    }
}
